import { times as _times, uniq as _uniq } from 'lodash-es'
import { Factory, Model } from 'miragejs'
import type { MockServer } from '@@/api/mock-server/server.js'
import {
  PROVET_PAY_PAYOUT_TRANSACTION_ENDPOINTS,
  PROVET_PAY_PAYOUT_TRANSACTION_RESOURCE_TYPES,
} from '@/constants/reports/provetPayPayoutTransaction.js'
import { filterable, pageable, sortable } from '../utils.js'
import { rand, randAmount, randNumber } from '@ngneat/falso'
import type { ProvetPayPayoutTransactionAttributes } from '@/models/reports/provetPayPayoutTransaction.js'
import { toNumber } from '@/utils/number.js'
import { formatToDateTimeISODate } from '@/utils/date/conversion.js'

const MOCK_KEY = 'provetPayPayoutTransaction'

const PAYMENT_METHODS = [
  'mastercard',
  'visa',
  'amex',
  'klarna',
  'ideal',
  'mc_applepay',
  'visa_applepay',
  'amex_googlepay',
] as const

export const model = Model.extend({})

export const factory = Factory.extend<
  ProvetPayPayoutTransactionAttributes & { payout_psp_reference: string }
>({
  currency() {
    return 'EUR'
  },
  fees() {
    const type = this.transaction_type
    if (type === 'Payment' || type === 'Refund') {
      const gross = Math.abs(toNumber(this.gross_amount))
      const feeRate = randNumber({ max: 300, min: 100 }) / 10000
      return -(gross * feeRate)
    }
    if (type === 'Chargeback') {
      return -randAmount({ max: 50, min: 5 })
    }
    return 0
  },
  gross_amount() {
    const type = this.transaction_type
    if (type === 'Payment') {
      return randAmount({ max: 5000, min: 10 })
    }
    return -randAmount({ max: 2000, min: 10 })
  },
  invoice_id() {
    const type = this.transaction_type
    if (type === 'FundTransfer' || type === 'Surcharge') {
      return null
    }
    return randNumber({ max: 99999, min: 1000 })
  },
  invoice_number() {
    const id = this.invoice_id
    if (id === null) {
      return null
    }
    return `INV-${id}`
  },
  net_payout_amount() {
    const gross = toNumber(this.gross_amount)
    const fees = Math.abs(toNumber(this.fees))
    return gross - fees
  },
  payment_method() {
    return rand([...PAYMENT_METHODS])
  },
  payment_transaction_id() {
    const type = this.transaction_type
    if (type === 'FundTransfer' || type === 'Surcharge') {
      return null
    }
    return `PSP_${randNumber({ max: 9999999, min: 1000000 })}`
  },
  payout_psp_reference() {
    return ''
  },
  transaction_ts_utc() {
    return ''
  },
  transaction_type() {
    const types = [
      'Payment',
      'Payment',
      'Payment',
      'Payment',
      'Payment',
      'Refund',
      'Chargeback',
      'ChargebackReversed',
      'FundTransfer',
      'Surcharge',
    ] as const
    return rand([...types])
  },
})

const randomTimeOnDate = (dateStr: string): string => {
  const hour = randNumber({ max: 18, min: 7 })
  const minute = randNumber({ max: 59, min: 0 })
  const paddedH = String(hour).padStart(2, '0')
  const paddedM = String(minute).padStart(2, '0')
  return `${dateStr}T${paddedH}:${paddedM}:00Z`
}

const subtractDays = (isoDate: string, days: number): string => {
  const dateOnly = isoDate.slice(0, 10)
  // eslint-disable-next-line provet-eslint/enforce-date-utils-usage
  const d = new Date(`${dateOnly}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() - days)
  return d.toISOString().slice(0, 10)
}

export const seeds = (server: MockServer) => {
  const payouts = server.schema.all('provetPayPayoutReport').models
  payouts.forEach((payout) => {
    const pspReference = payout.attrs.payout_psp_reference
    if (!pspReference) {
      return
    }
    const payoutDate = formatToDateTimeISODate(payout.attrs.payout_ts_utc)
    if (!payoutDate) {
      return
    }
    const txCount = toNumber(payout.attrs.transaction_count)

    _times(txCount).forEach(() => {
      const daysBack = randNumber({ max: 5, min: 0 })
      const txDate = subtractDays(payoutDate, daysBack)
      server.create(MOCK_KEY, {
        payout_psp_reference: pspReference,
        transaction_ts_utc: randomTimeOnDate(txDate),
      })
    })
  })
}

const includesInCommaSeparated = (
  itemValue: string | null | undefined,
  filterValue: string,
) =>
  filterValue
    .split(',')
    .some(
      (v) =>
        v.localeCompare(itemValue ?? '', undefined, { sensitivity: 'base' }) ===
        0,
    )

export const routes = (server: MockServer) => {
  server.get(
    PROVET_PAY_PAYOUT_TRANSACTION_ENDPOINTS.LIST,
    (schema, request) => {
      const pspReference = request.params.payout_psp_reference

      const all = schema.all(MOCK_KEY)
      const byPayout = all.filter(
        (item) => item.attrs.payout_psp_reference === pspReference,
      )

      const sorted = sortable(byPayout, request, {
        fees: (item) => toNumber(item.attrs.fees),
        gross_amount: (item) => toNumber(item.attrs.gross_amount),
        net_payout_amount: (item) => toNumber(item.attrs.net_payout_amount),
        transaction_ts_utc: (item) => item.attrs.transaction_ts_utc,
        transaction_type: (item) => item.attrs.transaction_type,
      })

      const filtered = filterable(sorted, request, {
        'transaction_type.in': (item, value) =>
          includesInCommaSeparated(item.attrs.transaction_type, value),
      })

      const { data, meta } = pageable(filtered, request)

      return {
        data: data.models.map((item) => ({
          id: item.id,
          attributes: {
            currency: item.attrs.currency,
            fees: item.attrs.fees,
            gross_amount: item.attrs.gross_amount,
            invoice_id: item.attrs.invoice_id,
            invoice_number: item.attrs.invoice_number,
            net_payout_amount: item.attrs.net_payout_amount,
            payment_method: item.attrs.payment_method,
            payment_transaction_id: item.attrs.payment_transaction_id,
            transaction_ts_utc: item.attrs.transaction_ts_utc,
            transaction_type: item.attrs.transaction_type,
          },
          type: PROVET_PAY_PAYOUT_TRANSACTION_RESOURCE_TYPES.TRANSACTION,
        })),
        meta: {
          pagination: meta,
        },
      }
    },
    { seed: 'ProvetPayPayoutReport' },
  )

  server.get(
    PROVET_PAY_PAYOUT_TRANSACTION_ENDPOINTS.FILTERS,
    (schema, request) => {
      const pspReference = request.params.payout_psp_reference

      const all = schema.all(MOCK_KEY)
      const byPayout = all.filter(
        (item) => item.attrs.payout_psp_reference === pspReference,
      )

      return {
        data: {
          id: 'filters',
          attributes: {
            transaction_type: _uniq(
              byPayout.models.map((item) =>
                String(item.attrs.transaction_type),
              ),
            ),
          },
          type: PROVET_PAY_PAYOUT_TRANSACTION_RESOURCE_TYPES.FILTERS,
        },
      }
    },
    { seed: 'ProvetPayPayoutReport' },
  )
}
